import { useEffect, useRef, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { BookOpenText, ArrowLeft, FileDown, Sparkles, MapPin, ListTodo, Link2, CalendarDays, StickyNote, Wand2, Users2 } from "lucide-react";
import { toast } from "sonner";
import api, { API } from "../lib/api";
import { auth } from "../lib/firebase";
import { DiaryDay, PlacesPanel, TasksPanel, LinksPanel, StoryPanel } from "../components/diary/DiaryPanels";
import OptimizePanel from "../components/diary/OptimizePanel";
import TripChat from "../components/diary/TripChat";

const TABS = [
  ["days", "Days", CalendarDays, "text-indigo-700 border-indigo-600 bg-indigo-50", "hover:text-indigo-700 hover:bg-indigo-50/60"],
  ["optimize", "Optimize", Wand2, "text-fuchsia-700 border-fuchsia-600 bg-fuchsia-50", "hover:text-fuchsia-700 hover:bg-fuchsia-50/60"],
  ["places", "Places", MapPin, "text-emerald-700 border-emerald-600 bg-emerald-50", "hover:text-emerald-700 hover:bg-emerald-50/60"],
  ["tasks", "Tasks", ListTodo, "text-amber-700 border-amber-600 bg-amber-50", "hover:text-amber-700 hover:bg-amber-50/60"],
  ["links", "Links", Link2, "text-cyan-700 border-cyan-600 bg-cyan-50", "hover:text-cyan-700 hover:bg-cyan-50/60"],
  ["story", "Travel Story", Sparkles, "text-rose-700 border-rose-600 bg-rose-50", "hover:text-rose-700 hover:bg-rose-50/60"],
];

/* Traveler Diary — auto-created for every trip. Planner before, companion during, journal after. */
export default function TripDiary() {
  const { id } = useParams();
  const [ov, setOv] = useState(null);
  const [tab, setTab] = useState("days");
  const [openDay, setOpenDay] = useState(null);
  const [note, setNote] = useState("");
  const [refreshTick, setRefreshTick] = useState(0);
  const versionRef = useRef(null);

  const load = useCallback(() => api.get(`/diary/${id}`).then((r) => setOv(r.data)).catch(() => setOv(null)), [id]);
  useEffect(() => { load(); }, [load]);

  // Live co-editing: poll a lightweight version marker; when a family member changes
  // anything, reload the overview and re-fetch the open day panels.
  const checkVersion = useCallback(async () => {
    try {
      const r = await api.get(`/diary/${id}/version`);
      const v = r.data.version || "";
      if (versionRef.current !== null && v && v !== versionRef.current) {
        load();
        setRefreshTick((t) => t + 1);
      }
      versionRef.current = v;
    } catch { /* transient — retry next tick */ }
  }, [id, load]);

  useEffect(() => {
    checkVersion(); // seed the baseline immediately so early co-editor changes aren't swallowed
    const iv = setInterval(() => { if (document.visibilityState === "visible") checkVersion(); }, 8000);
    const onVis = () => { if (document.visibilityState === "visible") checkVersion(); }; // catch edits made while tab was hidden
    document.addEventListener("visibilitychange", onVis);
    return () => { clearInterval(iv); document.removeEventListener("visibilitychange", onVis); };
  }, [checkVersion]);
  useEffect(() => {
    if (ov && !openDay) {
      const today = new Date().toISOString().slice(0, 10);
      const t = ov.days.find((d) => d.date === today);
      if (t) setOpenDay(t.date);
    }
  }, [ov]); // eslint-disable-line react-hooks/exhaustive-deps

  const pdf = async (path) => {
    const token = await auth.currentUser?.getIdToken();
    window.open(`${API}${path}?auth=${token}`, "_blank");
  };

  const quickNote = async () => {
    if (!note.trim()) return;
    try { await api.post(`/diary/${id}/note`, { text: note }); setNote(""); toast.success("Noted — added to today's diary"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not save note"); }
  };

  if (!ov) return <div className="max-w-5xl mx-auto px-5 py-16 text-sm text-muted-foreground">Loading your diary…</div>;
  const { trip } = ov;
  const readOnly = trip.my_role === "view";
  const STATUS = { planning: "Planning", upcoming: "Upcoming", traveling: "Traveling Now", completed: "Completed" };

  return (
    <div className="max-w-5xl mx-auto px-5 lg:px-8 py-8" data-testid="trip-diary">
      <Link to={`/trips/${id}`} className="inline-flex items-center gap-1 text-sm text-primary hover:underline mb-3" data-testid="diary-back"><ArrowLeft className="w-4 h-4" /> Back to trip</Link>
      <div className="border border-zinc-300 bg-gradient-to-br from-indigo-600 to-blue-700 text-white p-6 mb-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="text-xs font-mono uppercase tracking-wider opacity-80 flex items-center gap-1.5"><BookOpenText className="w-4 h-4" /> Traveler Diary</div>
            <h1 className="font-heading font-900 text-3xl mt-1">{trip.title}</h1>
            <div className="text-sm opacity-90 mt-1">{trip.start_date} → {trip.end_date} · {trip.num_days} day{trip.num_days === 1 ? "" : "s"} · {(trip.stops || []).join(" → ")}</div>
          </div>
          <span className="bg-white/20 backdrop-blur px-3 py-1.5 text-xs font-700 rounded-full" data-testid="diary-status">{STATUS[trip.status] || trip.status}</span>
        </div>
        {trip.my_role && trip.my_role !== "owner" && (
          <div className="inline-flex items-center gap-1.5 mt-3 bg-white/15 border border-white/30 px-3 py-1.5 text-xs font-600 rounded-full" data-testid="diary-shared-banner">
            <Users2 className="w-3.5 h-3.5" /> Shared by {trip.owner_name || "a member"} — you can {trip.my_role === "edit" ? "view & write in this diary" : "view (read-only)"}
          </div>
        )}
        <div className="flex flex-wrap gap-2 mt-4">
          <button onClick={() => pdf(`/diary/${id}/pdf`)} data-testid="diary-pdf-btn" className="inline-flex items-center gap-1.5 bg-white text-indigo-700 px-3 py-1.5 text-xs font-700"><FileDown className="w-3.5 h-3.5" /> Diary PDF (offline)</button>
          <button onClick={() => pdf(`/trips/${id}/pdf`)} data-testid="diary-trippack-btn" className="inline-flex items-center gap-1.5 bg-white/15 border border-white/40 px-3 py-1.5 text-xs font-700"><FileDown className="w-3.5 h-3.5" /> Trip Pack PDF</button>
          {Object.entries(ov.spend || {}).map(([c, v]) => <span key={c} className="bg-white/15 border border-white/30 px-3 py-1.5 text-xs font-700">Spent: {c} {Number(v).toLocaleString()}</span>)}
        </div>
      </div>

      {!readOnly && <div className="flex gap-2 mb-5" data-testid="diary-quick-note">
        <input value={note} onChange={(e) => setNote(e.target.value)} onKeyDown={(e) => e.key === "Enter" && quickNote()}
          placeholder="+ Quick note — timestamped into today's diary…" data-testid="diary-quick-note-input"
          className="flex-1 border border-zinc-300 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary bg-white" />
        <button onClick={quickNote} data-testid="diary-quick-note-save" className="inline-flex items-center gap-1.5 px-4 py-2 bg-primary text-white text-sm font-600"><StickyNote className="w-4 h-4" /> Note</button>
      </div>}

      <div className={trip.is_shared ? "lg:grid lg:grid-cols-[minmax(0,1fr)_320px] lg:gap-6 lg:items-start" : ""}>
        <div className="min-w-0">
          <div className="flex flex-wrap gap-1 border-b border-zinc-200 mb-5">
        {TABS.map(([t, label, Icon, activeCls, hoverCls]) => (
          <button key={t} onClick={() => setTab(t)} data-testid={`diary-tab-${t}`}
            className={`inline-flex items-center gap-1.5 px-3 py-2 text-sm font-700 border-b-2 -mb-px rounded-t transition-colors ${tab === t ? activeCls : `border-transparent text-muted-foreground ${hoverCls}`}`}>
            <Icon className="w-4 h-4" /> {label}
          </button>
        ))}
      </div>

        {tab === "days" && (
        <div className="space-y-3">
          {ov.days.map((d) => (
            <div key={d.date}>
              <button onClick={() => setOpenDay(openDay === d.date ? null : d.date)} data-testid={`diary-day-${d.date}`}
                className={`w-full text-left border bg-white px-4 py-3 flex items-center gap-3 hover:border-indigo-400 ${openDay === d.date ? "border-indigo-500" : "border-zinc-300"}`}>
                <div className="text-center shrink-0 w-12">
                  <div className="text-[10px] font-mono uppercase text-muted-foreground">Day</div>
                  <div className="font-heading font-900 text-2xl text-indigo-600 leading-none">{d.day_no}</div>
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-600 text-sm">{d.weekday}, {d.date}{d.move ? ` · ${d.move}` : d.city ? ` · ${d.city}` : ""}</div>
                  <div className="text-xs text-muted-foreground">
                    {d.planned ? `${d.activities} activit${d.activities === 1 ? "y" : "ies"}` : "No activities planned — Plan Your Day"}
                    {d.tasks_total > 0 && ` · Tasks ${d.tasks_done}/${d.tasks_total}`}
                    {d.has_entry && " · Diary written"}
                  </div>
                </div>
                {!d.planned && <span className="text-[10px] font-700 uppercase bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded shrink-0">Plan</span>}
              </button>
              {openDay === d.date && <DiaryDay tripId={id} date={d.date} city={d.city} onChanged={load} readOnly={readOnly} refreshSignal={refreshTick} />}
            </div>
          ))}
        </div>
        )}
        {tab === "optimize" && <OptimizePanel tripId={id} readOnly={readOnly} onChanged={load} />}
        {tab === "places" && <PlacesPanel tripId={id} readOnly={readOnly} />}
        {tab === "tasks" && <TasksPanel tripId={id} onChanged={load} readOnly={readOnly} />}
        {tab === "links" && <LinksPanel tripId={id} readOnly={readOnly} />}
        {tab === "story" && <StoryPanel tripId={id} />}
        </div>
        {trip.is_shared && (
          <aside className="mt-6 lg:mt-0 lg:sticky lg:top-20" data-testid="trip-chat-aside">
            <TripChat tripId={id} isOwner={!trip.my_role || trip.my_role === "owner"} />
          </aside>
        )}
      </div>
      <p className="mt-8 text-[11px] text-muted-foreground">Your diary is private to this trip's travellers — the owner and anyone the trip is shared with. It is never shown on public profiles or community pages.</p>
    </div>
  );
}
